import React, { useState, useEffect } from 'react';
import { Button } from './Button';
import { Input } from './Input';
import { IconButton } from './IconButton';
import { Popover } from './Popover';
import clsx from 'clsx';

// 상태 관리 외 함수
const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const getMonthName = (month: number) => `${month + 1}월`;
const getDaysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();

// 날짜 포맷 변경
const formatDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // 1자릿수일 때 0 붙여서 두자리로 표기
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// DatePicker 컴포넌트
interface DatePickerProps {
  dateRange?: boolean; // 날짜 선택 범위 여부
  value?: string | [string, string]; // 선택된 날짜 값
  onChange?: (value: string | [string, string] | null) => void; // 변경 시 호출되는 핸들러
  minDate?: string; // 최소 선택 가능 날짜
  maxDate?: string; // 최대 선택 가능 날짜
  width?: string | number; // 컴포넌트 너비
}

export const DatePicker: React.FC<DatePickerProps> = ({ dateRange, value, onChange, minDate, maxDate, width }) => {
  const [date, setDate] = useState<Date>(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(null);
  const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(null);
  const [pickerType, setPickerType] = useState<'date' | 'month' | 'year'>('date');
  const [inputValue, setInputValue] = useState<string>('');

  useEffect(() => {
    if (value) {
      if (dateRange && Array.isArray(value) && value.length === 2) {
        const [start, end] = value;
        const startDate = new Date(start);
        const endDate = new Date(end);
        setSelectedStartDate(startDate);
        setSelectedEndDate(endDate);
        setInputValue(`${formatDate(startDate)} ~ ${formatDate(endDate)}`);

        // 달력 선택된 년월로 업데이트
        setDate(new Date(startDate.getFullYear(), startDate.getMonth(), 1));
      } else if (!dateRange && typeof value === 'string') {
        const dateObj = new Date(value);
        setSelectedDate(dateObj);
        setInputValue(formatDate(dateObj));

        // 달력 선택된 년월로 업데이트
        setDate(new Date(dateObj.getFullYear(), dateObj.getMonth(), 1));
      }
    } else {
      setSelectedDate(null);
      setSelectedStartDate(null);
      setSelectedEndDate(null);
      setInputValue('');
    }
  }, [value, dateRange]);

  // 날짜가 유효한지 확인
  const isValidDate = (date: Date) => {
    if (minDate && date < new Date(minDate)) return false;
    if (maxDate && date > new Date(maxDate)) return false;
    return true;
  };

  // 월 전체가 비활성화 상태인지 확인
  const isMonthDisabled = (year: number, month: number) => {
    const daysInMonth = getDaysInMonth(year, month);
    for (let i = 1; i <= daysInMonth; i++) {
      const dayDate = new Date(year, month, i);
      if (isValidDate(dayDate)) {
        return false;
      }
    }
    return true;
  };

  // 년도 전체가 비활성화 상태인지 확인
  const isYearDisabled = (year: number) => {
    for (let i = 0; i < 12; i++) {
      if (!isMonthDisabled(year, i)) {
        return false;
      }
    }
    return true;
  };

  // 이전 버튼 클릭 핸들러
  const handlePrev = () => {
    const newDate =
      pickerType === 'year'
        ? new Date(date.getFullYear() - 12, date.getMonth(), 1)
        : new Date(date.getFullYear(), date.getMonth() - 1, 1);

    if (pickerType === 'year') {
      if (!isYearDisabled(newDate.getFullYear())) {
        setDate(newDate);
      }
    } else {
      if (!isMonthDisabled(newDate.getFullYear(), newDate.getMonth())) {
        setDate(newDate);
      }
    }
  };

  // 다음 버튼 클릭 핸들러
  const handleNext = () => {
    const newDate =
      pickerType === 'year'
        ? new Date(date.getFullYear() + 12, date.getMonth(), 1)
        : new Date(date.getFullYear(), date.getMonth() + 1, 1);

    if (pickerType === 'year') {
      if (!isYearDisabled(newDate.getFullYear())) {
        setDate(newDate);
      }
    } else {
      if (!isMonthDisabled(newDate.getFullYear(), newDate.getMonth())) {
        setDate(newDate);
      }
    }
  };

  const handleDateClick = (day: number) => {
    if (dateRange) {
      const newDate = new Date(date.getFullYear(), date.getMonth(), day);
      if (!selectedStartDate || (selectedStartDate && selectedEndDate)) {
        setSelectedStartDate(newDate);
        setSelectedEndDate(null);
      } else if (selectedStartDate && !selectedEndDate) {
        if (newDate < selectedStartDate) {
          setSelectedEndDate(selectedStartDate);
          setSelectedStartDate(newDate);
        } else {
          setSelectedEndDate(newDate);
        }
      }
    } else {
      setSelectedDate(new Date(date.getFullYear(), date.getMonth(), day));
    }
  };

  // 선택된 날짜가 범위 내에 있는지 확인
  const isInRange = (day: number) => {
    if (!dateRange || !selectedStartDate || !selectedEndDate) return false;
    const currentDate = new Date(date.getFullYear(), date.getMonth(), day);
    return currentDate >= selectedStartDate && currentDate <= selectedEndDate;
  };

  // 날짜 렌더링
  const renderDays = () => {
    const currentYear = date.getFullYear();
    const currentMonth = date.getMonth();
    const daysInCurrentMonth = getDaysInMonth(currentYear, currentMonth);
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();

    const prevMonth = new Date(currentYear, currentMonth - 1, 1);
    const daysInPrevMonth = getDaysInMonth(prevMonth.getFullYear(), prevMonth.getMonth());

    const days: JSX.Element[] = [];

    // 이전 달의 날짜 렌더링
    for (let i = firstDayOfMonth - 1; i >= 0; i--) {
      days.push(
        <div key={`prev-${i}`} className="dsx-DatePicker-day is-disabled">
          {daysInPrevMonth - i}
        </div>,
      );
    }
    // 이번달
    for (let i = 1; i <= daysInCurrentMonth; i++) {
      const currentDate = new Date(currentYear, currentMonth, i);
      const isDisabled = !isValidDate(currentDate);
      const isSelected =
        selectedDate &&
        selectedDate.getDate() === i &&
        selectedDate.getMonth() === currentMonth &&
        selectedDate.getFullYear() === currentYear;
      const isRangeStart =
        selectedStartDate &&
        selectedStartDate.getDate() === i &&
        selectedStartDate.getMonth() === currentMonth &&
        selectedStartDate.getFullYear() === currentYear;
      const isRangeEnd =
        selectedEndDate &&
        selectedEndDate.getDate() === i &&
        selectedEndDate.getMonth() === currentMonth &&
        selectedEndDate.getFullYear() === currentYear;
      const inRange = isInRange(i);
      days.push(
        <div
          key={i}
          className={clsx('dsx-DatePicker-day', {
            'is-selected': isSelected ?? isRangeStart ?? isRangeEnd,
            'is-disabled': isDisabled,
          })}
          data-range={isRangeStart ? 'start' : isRangeEnd ? 'end' : inRange ? 'in' : undefined}
          role="button"
          tabIndex={isDisabled ? -1 : 0}
          onClick={() => {
            if (!isDisabled) handleDateClick(i);
          }}
        >
          {i}
        </div>,
      );
    }
    // 다음달
    const totalCells = days.length;
    const remainingCells = 42 - totalCells; // 42 cells in a 6x7 grid
    for (let i = 1; i <= remainingCells; i++) {
      days.push(
        <div key={`next-${i}`} className="dsx-DatePicker-day is-disabled">
          {i}
        </div>,
      );
    }

    return days;
  };

  // 월 렌더링
  const renderMonths = () => {
    const months: JSX.Element[] = [];
    for (let i = 0; i < 12; i++) {
      const isDisabled = isMonthDisabled(date.getFullYear(), i);
      months.push(
        <div
          key={i}
          role="button"
          className={clsx('dsx-DatePicker-month', {
            'is-selected': date.getMonth() === i,
            'is-disabled': isDisabled,
          })}
          onClick={() => {
            if (!isDisabled) {
              setDate(new Date(date.getFullYear(), i, 1));
              setPickerType('date');
            }
          }}
          tabIndex={!isDisabled ? 0 : undefined}
        >
          {getMonthName(i)}
        </div>,
      );
    }
    return months;
  };

  // 년도 렌더링
  const renderYears = () => {
    const years: JSX.Element[] = [];
    const startYear = date.getFullYear() - 6; // 12년 범위
    for (let i = 0; i < 12; i++) {
      const year = startYear + i;
      const isDisabled = isYearDisabled(year);
      years.push(
        <div
          key={year}
          role="button"
          className={clsx('dsx-DatePicker-year', {
            'is-selected': date.getFullYear() === year,
            'is-disabled': isDisabled,
          })}
          onClick={() => {
            if (!isDisabled) {
              setDate(new Date(year, date.getMonth(), 1));
              setPickerType('date');
            }
          }}
          tabIndex={!isDisabled ? 0 : undefined}
        >
          {year}
        </div>,
      );
    }
    return years;
  };

  // Popover 상태관리
  const [popoverState, setPopoverState] = useState<boolean | undefined>(undefined);
  const changedPopoverState = (state: boolean | undefined) => {
    setPopoverState(state);
  };

  // 닫을 때 달력 아니면 다시 세팅
  useEffect(() => {
    if (!popoverState && pickerType !== 'date') {
      setPickerType('date');
    }
  }, [popoverState]);

  // 확인 버튼 클릭 핸들러
  const handleConfirm = () => {
    if (dateRange) {
      if (selectedStartDate && selectedEndDate) {
        const start = formatDate(selectedStartDate);
        const end = formatDate(selectedEndDate);
        setInputValue(`${start} ~ ${end}`);
        onChange && onChange([start, end]);
      }
    } else if (selectedDate) {
      const selected = formatDate(selectedDate);
      setInputValue(selected);
      onChange && onChange(selected);
    }
    setPopoverState(false);
  };

  // width 값이 있을 때만 style 객체 생성
  const triggerStyle = width ? { width } : undefined;

  return (
    <>
      <Popover
        trigger={
          <Input
            slotAfter={<IconButton name="calendar">날짜선택</IconButton>}
            placeholder={dateRange ? 'YYYY-MM-DD ~ YYYY-MM-DD' : 'YYYY-MM-DD'}
            className="dsx-DatepickerTrigger"
            value={inputValue}
            readOnly
            onChange={(e) => {
              setInputValue(e.target.value);
            }}
            style={triggerStyle}
          />
        }
        isOpen={popoverState}
        popoverState={changedPopoverState}
      >
        <div className="dsx-DatePicker">
          <div className="dsx-DatePicker-header" role="navigation">
            <IconButton
              name="chevronLeft"
              size="large"
              className="dsx-DatePicker-arrow"
              onClick={handlePrev}
              disabled={
                (pickerType === 'year' && isYearDisabled(date.getFullYear() - 12)) ||
                (pickerType !== 'year' && isMonthDisabled(date.getFullYear(), date.getMonth() - 1))
              }
            >
              이전
            </IconButton>
            <button
              className="dsx-DatePicker-label dsx-DatePicker-label--year"
              onClick={() => {
                setPickerType('year');
              }}
            >
              {`${date.getFullYear()}년`}
            </button>
            <button
              className="dsx-DatePicker-label dsx-DatePicker-label--month"
              onClick={() => {
                setPickerType('month');
              }}
            >
              {getMonthName(date.getMonth())}
            </button>
            <IconButton
              name="chevron"
              size="large"
              className="dsx-DatePicker-arrow"
              onClick={handleNext}
              disabled={
                (pickerType === 'year' && isYearDisabled(date.getFullYear() + 12)) ||
                (pickerType !== 'year' && isMonthDisabled(date.getFullYear(), date.getMonth() + 1))
              }
            >
              다음
            </IconButton>
          </div>
          {pickerType === 'date' && (
            <>
              <div className="dsx-DatePicker-body">
                <div className="dsx-DatePicker-dayNames">
                  {daysOfWeek.map((day, index) => (
                    <div key={index} className="dsx-DatePicker-dayName">
                      {day}
                    </div>
                  ))}
                </div>
                <div className="dsx-DatePicker-days">{renderDays()}</div>
              </div>
              <div className="dsx-DatePicker-controls">
                <Button
                  onClick={() => {
                    setPopoverState(false);
                  }}
                >
                  취소
                </Button>
                <Button variant="primary" onClick={handleConfirm}>
                  확인
                </Button>
              </div>
            </>
          )}
          {pickerType === 'month' && (
            <div className="dsx-DatePicker-body">
              <div className="dsx-DatePicker-months">{renderMonths()}</div>
            </div>
          )}
          {pickerType === 'year' && (
            <div className="dsx-DatePicker-body">
              <div className="dsx-DatePicker-years">{renderYears()}</div>
            </div>
          )}
        </div>
      </Popover>
    </>
  );
};
